import React from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
} from "@heroicons/react/24/outline";

interface PdfFloatingNavProps {
  currentPage: number;
  numPages: number;
  scale: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomFit?: () => void;
  minScale: number;
  maxScale: number;
  /** Hide the page controls for single-image viewers (evidence photos). */
  showPages?: boolean;
}

const PdfFloatingNav: React.FC<PdfFloatingNavProps> = ({
  currentPage,
  numPages,
  scale,
  onPrevPage,
  onNextPage,
  onZoomIn,
  onZoomOut,
  onZoomFit,
  minScale,
  maxScale,
  showPages = true,
}) => {
  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 bg-base-100/90 backdrop-blur-sm rounded-xl shadow-lg border border-base-300 px-3 py-1.5">
      {/* Page navigation */}
      {showPages && (
        <div className="flex items-center gap-1">
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={onPrevPage}
            disabled={currentPage <= 1}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          >
            <ChevronLeftIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
          </button>
          <span className="text-xs font-medium text-gray-700 dark:text-base-content/80 min-w-[4.5rem] text-center">
            Page {currentPage} of {numPages}
          </span>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={onNextPage}
            disabled={currentPage >= numPages}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          >
            <ChevronRightIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
          </button>
        </div>
      )}

      {showPages && <div className="w-px h-4 bg-gray-300 dark:bg-base-300" />}

      {/* Zoom controls */}
      <div className="flex items-center gap-1">
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={onZoomOut}
          disabled={scale <= minScale}
          className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
        >
          <MagnifyingGlassMinusIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
        </button>
        {onZoomFit ? (
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={onZoomFit}
            className="text-xs font-medium text-gray-700 dark:text-base-content/80 min-w-[2.5rem] text-center hover:bg-gray-100 dark:hover:bg-base-200 rounded-md py-1 transition-colors"
          >
            {Math.round(scale * 100)}%
          </button>
        ) : (
          <span className="text-xs font-medium text-gray-700 dark:text-base-content/80 min-w-[2.5rem] text-center">
            {Math.round(scale * 100)}%
          </span>
        )}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={onZoomIn}
          disabled={scale >= maxScale}
          className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
        >
          <MagnifyingGlassPlusIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
        </button>
      </div>
    </div>
  );
};

export default PdfFloatingNav;
